/**
 * Hedita Enterprise - Team Feature Types
 * Team Members, Ruoli, Permessi (Nuovo Schema admin_id)
 */

// ============================================================
// TABELLA: team_members (Nuova struttura Enterprise)
// ============================================================
export interface TeamMember {
  id: string;
  admin_id: string;
  auth_user_id?: string;
  email: string;
  nome: string;
  cognome?: string;
  telefono?: string;
  ruolo: RuoloTeam;
  permissions: Permissions;
  is_visible_on_guest_app: boolean;
  guest_app_bio?: string;
  guest_app_foto_url?: string;
  assigned_properties: string[];
  attivo: boolean;
  ultimo_accesso?: string;
  note?: string;
  created_at: string;
  updated_at: string;
}

export type RuoloTeam = 
  | 'direttore' 
  | 'receptionist' 
  | 'housekeeper' 
  | 'tecnico' 
  | 'osservatore' 
  | 'host';

export const RUOLI_OPTIONS = [
  { value: "direttore" as RuoloTeam, label: "Direttore / GM", desc: "Gestione operativa completa. Vede finanze, prenotazioni, ospiti.", livello: 80 },
  { value: "receptionist" as RuoloTeam, label: "Receptionist", desc: "Gestisce prenotazioni, check-in/out, ospiti e messaggi.", livello: 50 },
  { value: "housekeeper" as RuoloTeam, label: "Housekeeper", desc: "Attività pulizie e stato camere.", livello: 30 },
  { value: "tecnico" as RuoloTeam, label: "Tecnico / Manutenzione", desc: "Gestisce ticket manutenzione e interventi.", livello: 30 },
  { value: "host" as RuoloTeam, label: "Host / Accoglienza", desc: "Visibile nella Guest App. Accoglie gli ospiti.", livello: 40 },
  { value: "osservatore" as RuoloTeam, label: "Osservatore", desc: "Sola lettura. Per supervisori esterni.", livello: 10 },
] as const;

export interface Permissions {
  vede_finanze: boolean;
  vede_messaggi: boolean;
  vede_notifiche: boolean;
  gestisce_prenotazioni: boolean;
  gestisce_pulizie: boolean;
  gestisce_manutenzione: boolean;
  gestisce_ospiti: boolean;
  gestisce_strutture: boolean;
  gestisce_team: boolean;
  gestisce_impostazioni: boolean;
}

export const DEFAULT_PERMISSIONS: Record<RuoloTeam, Permissions> = {
  direttore: {
    vede_finanze: true, vede_messaggi: true, vede_notifiche: true,
    gestisce_prenotazioni: true, gestisce_pulizie: true, gestisce_manutenzione: true,
    gestisce_ospiti: true, gestisce_strutture: true, gestisce_team: false, gestisce_impostazioni: false,
  },
  receptionist: {
    vede_finanze: false, vede_messaggi: true, vede_notifiche: true,
    gestisce_prenotazioni: true, gestisce_pulizie: false, gestisce_manutenzione: false,
    gestisce_ospiti: true, gestisce_strutture: false, gestisce_team: false, gestisce_impostazioni: false,
  },
  housekeeper: {
    vede_finanze: false, vede_messaggi: false, vede_notifiche: true,
    gestisce_prenotazioni: false, gestisce_pulizie: true, gestisce_manutenzione: false,
    gestisce_ospiti: false, gestisce_strutture: false, gestisce_team: false, gestisce_impostazioni: false,
  },
  tecnico: {
    vede_finanze: false, vede_messaggi: false, vede_notifiche: true,
    gestisce_prenotazioni: false, gestisce_pulizie: false, gestisce_manutenzione: true,
    gestisce_ospiti: false, gestisce_strutture: false, gestisce_team: false, gestisce_impostazioni: false,
  },
  host: {
    vede_finanze: false, vede_messaggi: true, vede_notifiche: true,
    gestisce_prenotazioni: false, gestisce_pulizie: false, gestisce_manutenzione: false,
    gestisce_ospiti: true, gestisce_strutture: false, gestisce_team: false, gestisce_impostazioni: false,
  },
  osservatore: {
    vede_finanze: false, vede_messaggi: true, vede_notifiche: true,
    gestisce_prenotazioni: false, gestisce_pulizie: false, gestisce_manutenzione: false,
    gestisce_ospiti: false, gestisce_strutture: false, gestisce_team: false, gestisce_impostazioni: false,
  },
};

export const LABELS_PERMISSIONS: Record<keyof Permissions, string> = {
  vede_finanze: "Vede Finanze",
  vede_messaggi: "Vede Messaggi",
  vede_notifiche: "Vede Notifiche",
  gestisce_prenotazioni: "Gestisce Prenotazioni",
  gestisce_pulizie: "Gestisce Pulizie",
  gestisce_manutenzione: "Gestisce Manutenzione",
  gestisce_ospiti: "Gestisce Ospiti",
  gestisce_strutture: "Gestisce Strutture",
  gestisce_team: "Gestisce Team",
  gestisce_impostazioni: "Gestisce Impostazioni",
};

export interface Admin {
  id: string;
  auth_user_id: string;
  email: string;
  nome?: string;
  cognome?: string;
  telefono?: string;
  piano: 'starter' | 'premium' | 'business';
  piano_scadenza?: string;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface TeamMemberInsert {
  email: string;
  nome: string;
  cognome?: string;
  telefono?: string;
  password?: string;
  ruolo?: RuoloTeam;
  assigned_properties?: string[];
  attivo?: boolean;
  note?: string;
  permissions?: Partial<Permissions>;
  is_visible_on_guest_app?: boolean;
  guest_app_bio?: string;
  guest_app_foto_url?: string;
}

export interface TeamMemberUpdate extends Partial<TeamMemberInsert> {}

// Aliases retro-compatibilità
/** @deprecated Usa TeamMember */
export type Collaboratore = TeamMember;
/** @deprecated Usa TeamMemberInsert */
export type CollaboratoreInsert = TeamMemberInsert;
/** @deprecated Usa Permissions */
export type PermessiCollaboratore = Permissions;
/** @deprecated Usa DEFAULT_PERMISSIONS */
export const PERMESSI_DEFAULT = DEFAULT_PERMISSIONS;
/** @deprecated Usa LABELS_PERMISSIONS */
export const LABELS_PERMESSI = LABELS_PERMISSIONS;
